import { Chip } from '@/app/components/Chip'
import { ROUTES } from '@/app/lib/constants'
import { ApplicationStatus, ApplicationTerm } from '@/types/application'
import { OrderPaymentTerm, OrderStatus } from '@/types/order'
import Link from 'next/link'
import React from 'react'
import { useCourseApplicationStatus } from '../hooks/useCourseApplicationStatus'
import { ICourse } from '@/types/course'
import { useTranslations } from 'next-intl'

type AlreadyAppliedAlertProps = {
  courseInfo: Pick<ICourse, '_id'>
} & React.HTMLAttributes<HTMLDivElement>

const AlreadyAppliedAlert = ({
  courseInfo,
  ...props
}: AlreadyAppliedAlertProps) => {
  const { orders, applications, isLoading } = useCourseApplicationStatus({
    courseInfo,
  })

  const t = useTranslations('CoursePage')

  if (isLoading || !Array.isArray(orders) || !Array.isArray(applications)) {
    return null
  }

  const hasNoCancelledScoringPayApplications = applications.some(
    (application) =>
      (application.applicationTerm === ApplicationTerm.FixPay ||
        application.applicationTerm === ApplicationTerm.Isa) &&
      application.statuses &&
      application.statuses.at(-1) !== ApplicationStatus.Cancelled
  )

  const hasPendingApplication = applications.some(
    (application) => application.statuses?.at(-1) === ApplicationStatus.Applied
  )
  const hasCompletedScoringOrder = orders.some(
    (order) =>
      (order.paymentTerm === OrderPaymentTerm.FixPay ||
        order.paymentTerm === OrderPaymentTerm.Isa) &&
      order.status === OrderStatus.Completed
  )
  const hasActiveOrders =
    orders.length > 0 &&
    orders.every((order) => order.status !== OrderStatus.Cancelled)

  const summary =
    (hasNoCancelledScoringPayApplications || hasPendingApplication) &&
    !hasCompletedScoringOrder
      ? {
          label: t('review_application_status'),
          cta: (
            <>
              {t('go_to')}{' '}
              <Link className="underline" href={ROUTES.myOrders}>
                {t('orders')}
              </Link>
              .
            </>
          ),
        }
      : hasNoCancelledScoringPayApplications && hasCompletedScoringOrder
        ? {
            label: t('applied'),
            cta: (
              <>
                {t('view_order')}{' '}
                <Link className="underline" href={ROUTES.myOrders}>
                  {t('here')}
                </Link>
                .
              </>
            ),
          }
        : hasActiveOrders
          ? {
              label: t('already_bought_course'),
              cta: (
                <>
                  {t('view_order')}{' '}
                  <Link className="underline" href={ROUTES.myOrders}>
                    {t('here')}
                  </Link>
                  .
                </>
              ),
            }
          : null

  if (!summary) {
    return null
  }

  return (
    <div
      {...props}
      className={`flex items-center justify-center gap-4 md:gap-2 p-1 text-xs font-sans font-medium leading-4 border-t text-mydra-black bg-mydra-lighter-orange md:flex md:pr-3 ${props.className ?? ''}`}
    >
      <Chip size="small" className="!bg-mydra-light-orange !rounded-2xl">
        {summary.label}
      </Chip>
      <p>{summary.cta}</p>
    </div>
  )
}

export default AlreadyAppliedAlert
